/* eslint-disable react/no-danger */
/**
 * Introduce
 * 
 * @Author: Focci
 * @Date: 2023-08-07 10:15:42
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-07 10:15:42
 */

'use client'

import PropTypes from 'prop-types'
import Folding from '@comp/Folding'
import useTracking from '@hook/useTracking'
import PropertyIcons from '@page/common/card/property/PropertyIcons'
import Image from 'next/image'
import { propertyType } from '@lib/config'
import { formatLang, isWithinMonth } from '@lib/datetime'
import { houseToGta, isAU } from '@lib/utils'
import { isArray } from 'lodash-es'
import { useLocale, useTranslations } from 'next-intl'
import FavoriteAndShare from './FavoriteAndShare'
import ForAdmin from './ForAdmin'
import LoanAndNoteBar from './LoanAndNoteBar'
import RentPublishAlert from './RentPublishAlert'
import PriceGuidePDF from './PriceGuidePDF'
import MediaIframe from './MediaIframe'

export default function Introduce({
  house = {},
  showNote = false, // 是否显示看房笔记
  showLoan = false // 是否显示贷款计算器
}) {
  const locale = useLocale()
  const th = useTranslations('house')
  const tp = useTranslations('publish')

  // 室内设施
  const furnitures = house.publisher && isArray(house?.rental?.furniture)
    ? house.rental.furniture
    : []

  const isSold = house.typeId === propertyType.SOLD
  const isEstimate = house.typeId === propertyType.ESTIMATE
  const isSoldAndEstimate = isEstimate || isSold
  const hasSoldInfo = isSoldAndEstimate && !!house.soldInfo

  const isDraftRental = house.typeId === propertyType.RENTAL && house.status !== 1

  const { pushTrack } = useTracking()
  function handleToggle(expanding) {
    if (expanding && isAU) {
      pushTrack('homely_desc_read_more_click', houseToGta(house))
    }
  }

  return (
    <>
      <div
        data-modulename={th('baseInfo')}
        id="module-baseInfo"
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          {/* 售出价格 */}
          {(hasSoldInfo || Boolean(house.priceLabel)) && (
            <h2 className="text-base md:text-f1.3 text-[#ff5533] font-medium mb-1">
              {hasSoldInfo ? `${th('soldPrice')}: ${house.soldInfo?.sold_price}` : house.priceLabel}
            </h2>
          )}
          {/* 如果是售出或者估价，且有售出日期 且为6个月内售出 */}
          {isSoldAndEstimate && house?.soldDate && isWithinMonth(house.soldDate) && (
            <div className="mb-3">
              {formatLang(house.soldDate, th('soldDate'), locale)}
            </div>
          )}
        </div>
        {!isDraftRental && <FavoriteAndShare house={house} />}
        {isDraftRental && <RentPublishAlert houseId={house.houseId} />}
      </div>
      <PriceGuidePDF house={house} />
      {house.address && (
        <h1 className="text-f.9 md:text-f1.2 mt-2 font-medium">
          {house.address}
        </h1>
      )}
      <div className="mt-2 flex flex items-center text-f.8 flex-wrap gap-5">
        <PropertyIcons
          data={house}
          iconWidth="1.3rem"
          className="gap-5 text-f.8"
        />
        {!isSoldAndEstimate && (
          <>
            {house.category && <span>{house.category.ch}</span>}
            {house.listAtLabel && <span>{house.listAtLabel}</span>}
            <ForAdmin house={house} />
          </>
        )}
      </div>
      {/* tag */}
      {isArray(house.tags) && house.tags.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {house.tags.map((tag, index) => (
            <span
              key={index}
              className="border border-[#6F7485] rounded-sm px-1 py-0.5"
            >
              {tag.label}
            </span>
          ))}
        </div>
      )}
      <LoanAndNoteBar
        showNote={showNote}
        showLoan={showLoan}
        house={house}
      />
      {/* 标题 */}
      {!isSoldAndEstimate && (
        <h2 className="mt-4 text-base font-medium leading-tight">
          {house.h1}
        </h2>
      )}
      {/* 室内设施 */}
      {furnitures.length > 0 && (
        <div className="my-6">
          <div className="font-medium">
            {tp('InmoomFacilities')}
          </div>
          <div className="flex flex-wrap gap-x-6 md:gap-x-12 gap-y-4 mt-4">
            {furnitures.map((item) => (
              <div key={item.label}>
                <Image
                  unoptimized
                  src={item.icon || ''}
                  width={30}
                  height={30}
                  alt={item.label}
                />
                <div>{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* 描述 */}
      {house.description && (
        <div className="mt-4 text-f.8">
          <Folding onToggle={handleToggle} triggerClassName="flex justify-center md:justify-start">
            <div
              dangerouslySetInnerHTML={{ __html: house.description }}
            />
          </Folding>
        </div>
      )}
      <MediaIframe house={house} />
      {/* {house.videoUrl && (
        <Waypoint onEnter={() => getWedget(() => import('./VideoIntro'))}>
          <div className="aspect-video w-full bg-cf0 mt-4">
            <VideoIntro videoUrl={house.videoUrl} />
          </div>
        </Waypoint>
      )} */}
    </>
  )
}

Introduce.propTypes = {
  house: PropTypes.object,
  showNote: PropTypes.bool,
  showLoan: PropTypes.bool,
}
